import type { RouteRecordRaw } from 'vue-router';
import { defineComponent } from 'vue';
import { NovaAuthorizationCapability } from '@/composables/useNovaAuthorization';
import { FeatureFlags } from '@/plugins/launchDarkly/featureFlags';

// Placeholder component for external routes
const PlaceholderComponent = defineComponent({
  template: `
    <div class="q-pa-md">
      <h4>Page Under Development</h4>
      <p>This page is currently under development.</p>
    </div>
  `
});

export const settingsRoutes: RouteRecordRaw[] = [
  {
    path: '/account',
    name: 'Account',
    redirect: { name: 'SettingsAccount' },
    meta: {
      requiresAuth: true,
      navigation: {
        label: 'Account & Help',
        icon: 'account-icon.svg', // Changed from settings-icon.svg to existing account-icon.svg
        order: 6,
        showInMenu: true,
        permissions: {
          rbacRoles: ['accounthelpnav-read']
        }
      }
    },
    children: [
      {
        path: 'account/',
        name: 'SettingsAccount',
        component: () => import("@/views/settings/account/Account.vue"),
        props: {
          actionPermissions: ["accountsettings-write", "accountsettings-all"],
          viewPermissions: ["accountsettings-read", "accountsettings-all"],
        },
        meta: {
          title: "Settings - Account",
          requiresAuth: true,
          navigation: {
            parent: 'Account & Help',
            parentIcon: 'account-icon.svg',
            label: 'Account',
            order: 1,
            showInMenu: true,
            permissions: {
              rbacRoles: ['accountsettings-read', 'accountsettings-all'],
              ownerRoles: ['Publisher', 'Retailer'],
              viewPermissions: ['accountsettings-read', 'accountsettings-all'],
              actionPermissions: ['accountsettings-write', 'accountsettings-all']
            }
          }
        }
      },
      {
        path: 'account/account-edit/',
        name: 'SettingsAccountEdit',
        component: () => import("@/views/settings/account-edit/AccountEdit.vue"),
        props: {
          actionPermissions: ["accountsettings-write", "accountsettings-all"],
          viewPermissions: ["accountsettings-read", "accountsettings-all"],
          navigationUrl: "/account/account/account-edit",
        },
        meta: {
          title: "Settings - Account Edit",
          requiresAuth: true,
        }
      },
      {
        path: '/account-and-help/developer/developer-docs',
        name: 'SettingsDocumentation',
        component: () =>
          import("@/views/settings/documentation/Documentation.vue"),
        props: { viewPermissions: ["developerguide-read"] },
        meta: {
          title: "Settings - Documentation",
          requiresAuth: true,
          navigation: {
            parent: 'Account & Help',
            parentIcon: 'account-icon.svg',
            label: 'Developer',
            order: 4,
            showInMenu: true,
            permissions: {
              ownerRoles: ['Publisher', 'Retailer'],
              rbacRoles: ['developerguide-read', 'developerguide-all'],
              viewPermissions: ['developerguide-read']
            }
          }
        }
      },
      {
        path: 'certificates/',
        name: 'SettingsCertificate',
        component: () => import("@/views/settings/certificate/Certificate.vue"),
        props: {
          actionPermissions: ["certificate-write", "certificate-all"],
          viewPermissions: ["certificate-read", "certificate-all"],
        },
        meta: {
          title: "Settings - Certificate",
          requiresAuth: true,
          navigation: {
            parent: 'Account & Help',
            parentIcon: 'account-icon.svg',
            label: 'Certificates',
            order: 5,
            showInMenu: true,
            permissions: {
              ownerRoles: ['Retailer'],
              rbacRoles: ['certificate-read', 'certificate-all'],
              viewPermissions: ['certificate-read', 'certificate-all']
            }
          }
        }
      },
      {
        path: 'developer/webhooks/',
        name: 'SettingsWebhooks',
        component: () => import("@/views/settings/webhooks/Webhooks.vue"),
        props: {
          actionPermissions: ["webhooks-write", "webhooks-all"],
          viewPermissions: ["webhooks-read", "webhooks-all"],
        },
        meta: {
          title: "Settings - Webhooks",
          requiresAuth: true,
        }
      },
      {
        path: 'developer/jit-endpoints/',
        name: 'SettingsJitEndpoints',
        component: () =>
          import("@/views/settings/jit-endpoints/JitEndpoints.vue"),
        props: {
          actionPermissions: ["jitsettings-write", "jitsettings-all"],
          viewPermissions: ["jitsettings-read", "jitsettings-all"],
        },
        meta: {
          title: "Settings - JIT Endpoints",
          requiresAuth: true,
        }
      },
      {
        path: 'external-references/',
        name: 'SettingsExternalReferences',
        component: () =>
          import("@/views/settings/external-references/ExternalReferences.vue"),
        props: {
          actionPermissions: ["product-all", "product-write", "product-delete"],
          viewPermissions: ["product-read", "product-all"],
        },
        meta: {
          title: "Settings - External References",
          requiresAuth: true,
          navigation: {
            parent: 'Account & Help',
            parentIcon: 'account-icon.svg',
            label: 'External References',
            order: 6,
            showInMenu: true,
            permissions: {
              ownerRoles: ['Publisher', 'Retailer'],
              viewPermissions: ['product-read', 'product-all'],
              actionPermissions: ['product-all', 'product-write', 'product-delete']
            }
          }
        }
      },
      {
        path: '/account-and-help/notification-settings/',
        name: 'Notification Settings',
        component: PlaceholderComponent,
        meta: {
          title: "Notification Settings",
          requiresAuth: true,
          navigation: {
            parent: 'Account & Help',
            parentIcon: 'account-icon.svg',
            label: 'Notification Settings',
            order: 7,
            showInMenu: true,
            featureFlag: FeatureFlags.NOTIFICATION_SETTINGS,
            permissions: {
              ownerRoles: ['Publisher', 'Retailer']
            }
          }
        }
      },
    ]
  },
  {
    path: "/usersandgroups/users/",
    name: "SettingsUsers",
    component: () => import("@/views/settings/users/Users.vue"),
    props: {
      actionPermissions: ["user-write", "user-all"],
      viewPermissions: ["user-read", "user-all"],
    },
    meta: {
      title: "Settings - Users",
      requiresAuth: true,
      navigation: {
        parent: 'Account & Help',
        parentIcon: 'account-icon.svg',
        label: 'Users',
        order: 2,
        showInMenu: true,
        permissions: {
          ownerRoles: ['Publisher', 'Retailer'],
          rbacRoles: ['user-read', 'user-all'],
          viewPermissions: ['user-read', 'user-all'],
          actionPermissions: ['user-write', 'user-all']
        }
      }
    },
  },
  {
    path: "/usersandgroups/archived-users/",
    name: "SettingsArchivedUsers",
    component: () => import("@/views/settings/archived-users/ArchivedUsers.vue"),
    props: {
      actionPermissions: ["user-write", "user-all"],
      viewPermissions: ["user-read", "user-all"],
    },
    meta: {
      title: "Settings - Archived Users",
      requiresAuth: true,
      navigation: {
        featureFlag: FeatureFlags.USER_ARCHIVE,
      }
    },
  },
  {
    path: "/distribution/distributionsettings/currencies/",
    name: "SettingsMarkets",
    component: () => import("@/views/settings/markets/Markets.vue"),
    props: {
      actionPermissions: ["currencysettings-write", "currencysettings-all"],
      viewPermissions: ["currencysettings-read", "currencysettings-all"],
    },
    meta: {
      title: "Settings - Markets",
      requiresAuth: true,
      navigation: {
        parent: 'Account & Help',
        parentIcon: 'account-icon.svg',
        label: 'Markets',
        order: 99,
        showInMenu: false,
        permissions: {
          ownerRoles: ['Publisher'],
          viewPermissions: ['currencysettings-read', 'currencysettings-all'],
          actionPermissions: ['currencysettings-write', 'currencysettings-all']
        }
      }
    },
  },
  {
    path: "/account-and-help/wallet-provider/",
    name: "WalletProvider",
    component: PlaceholderComponent,
    props: {
      viewPermissions: ["dashboardnav-read"],
    },
    meta: {
      title: "Account & Help - Wallet Provider",
      requiresAuth: true,
      navigation: {
        parent: 'Account & Help',
        parentIcon: 'account-icon.svg',
        label: 'Wallet Provider',
        order: 3,
        showInMenu: true,
        featureFlag: FeatureFlags.PAYMENTS_INTEGRATION,
        requiredCapability: NovaAuthorizationCapability.WALLET_PROVIDER_READ,
        permissions: {
          ownerRoles: ['Publisher', 'Retailer']
        }
      }
    },
  },
  {
    path: "/distribution/distributionsettings/regions/",
    name: "MyVaultRegions",
    component: () => import("@/views/my_vault/regions/Regions.vue"),
    props: {
      actionPermissions: ["region-all", "region-write", "region-delete"],
      viewPermissions: ["region-read", "region-all"],
    },
    meta: {
      title: "My Vault - Regions",
      requiresAuth: true,
      navigation: {
        parent: 'Distribution',
        parentIcon: 'dollar-icon.svg',
        label: 'Distribution Settings',
        order: 6,
        showInMenu: true,
        permissions: {
          rbacRoles: ['region-read', 'region-all'],
          ownerRoles: ['Publisher'],
          actionPermissions: ['region-all', 'region-write', 'region-delete'],
          viewPermissions: ['region-read', 'region-all']
        }
      }
    }
  },
  {
    path: "/distribution/distributionsettings/regions-add/",
    name: "MyVaultRegionsAdd",
    component: () => import("@/views/my_vault/regions-add/RegionsAdd.vue"),
    props: {
      actionPermissions: ["region-all", "region-write"],
      viewPermissions: ["region-read", "region-all"],
    },
    meta: {
      title: "My Vault - Add Region",
      requiresAuth: true,
    }
  },
  {
    path: "/distribution/distributionsettings/regions-edit/:id",
    name: "MyVaultRegionsEdit",
    component: () => import("@/views/my_vault/regions-edit/RegionsEdit.vue"),
    props: {
      actionPermissions: [
        "region-all",
        "region-write",
        "region-delete",
        "regionstatus-write",
      ],
      viewPermissions: ["region-read", "region-all"],
    },
    meta: {
      title: "My Vault - Edit Region",
      requiresAuth: true,
    }
  },
  {
    path: "/distribution/distributionsettings/regions-copy/:id",
    name: "MyVaultRegionsCopy",
    component: () => import("@/views/my_vault/regions-copy/RegionsCopy.vue"),
    props: {
      actionPermissions: ["region-all", "region-write"],
      viewPermissions: ["region-read", "region-all"],
    },
    meta: {
      title: "My Vault - Copy Region",
      requiresAuth: true,
    }
  },
  {
    path: "/distribution/distributionsettings/platforms/",
    name: "MyVaultPlatforms",
    component: () => import("@/views/settings/platform/PlatformSettings.vue"),
    props: {
      actionPermissions: ["platformsettings-write", "platformsettings-all"],
      viewPermissions: ["platformsettings-read", "platformsettings-all"],
    },
    meta: {
      title: "My Vault - Platforms",
      requiresAuth: true,
      navigation: {
        parent: 'Distribution',
        parentIcon: 'dollar-icon.svg',
        label: 'Distribution Settings',
        order: 7,
        showInMenu: false,
        featureFlag: FeatureFlags.DISTRIBUTION_SETTINGS_PLATFORM,
        permissions: {
          rbacRoles: ['platformsettings-read', 'platformsettings-all'],
          ownerRoles: ['Publisher'],
          actionPermissions: ['platformsettings-write', 'platformsettings-all'],
          viewPermissions: ['platformsettings-read', 'platformsettings-all']
        }
      }
    }
  },
];
